import { CONFIG } from '../config/constants.js';
import { makeWorkerSick } from './workerSystem.js';

/**
 * Checks if a disease outbreak occurs
 * @param {Object} gameState - Current game state
 * @returns {Object|null} Disease event or null if no outbreak
 */
export function checkDiseaseOutbreak(gameState) {
  const aliveWorkers = gameState.workers.filter(
    w => w.status !== 'dead'
  );

  // Need at least 3 workers for disease to matter
  if (aliveWorkers.length < 3) return null;

  // Base disease chance
  let diseaseChance = CONFIG.DISEASE_CHANCE_PER_DAY;

  // Hospital reduces disease chance by 80%
  const hasHospital = gameState.buildings.some(
    b => b.type === 'hospital' && b.complete
  );
  if (hasHospital) {
    diseaseChance *= 0.2;
  }

  // Low morale increases disease chance by 50%
  if (gameState.morale < 40) {
    diseaseChance *= 1.5;
  }

  const roll = Math.random() * 100;

  if (roll < diseaseChance) {
    // 1-2 workers get sick
    const sickCount = Math.floor(Math.random() * 2) + 1;
    const healthyWorkers = aliveWorkers.filter(w => w.status === 'healthy');

    if (healthyWorkers.length === 0) return null;

    const victimsCount = Math.min(sickCount, healthyWorkers.length);
    const victims = [];

    // Randomly select victims
    const availableWorkers = [...healthyWorkers];
    for (let i = 0; i < victimsCount; i++) {
      const randomIndex = Math.floor(Math.random() * availableWorkers.length);
      victims.push(availableWorkers.splice(randomIndex, 1)[0]);
    }

    return {
      type: 'disease_outbreak',
      name: 'Disease Outbreak',
      icon: '🦠',
      message: `${victimsCount} worker${victimsCount > 1 ? 's' : ''} fell ill!`,
      victims: victims.map(v => v.id)
    };
  }

  return null;
}

/**
 * Applies disease to specified workers
 * @param {Array} workers - Worker array
 * @param {Array} victimIds - IDs of workers to infect
 * @returns {Array} Updated workers
 */
export function applyDisease(workers, victimIds) {
  let updated = workers;
  victimIds.forEach(id => {
    updated = makeWorkerSick(updated, id);
  });
  return updated;
}

/**
 * Creates a disease event for the event system
 * @returns {Object} Disease event definition
 */
export function createDiseaseEvent() {
  return {
    roll: null, // Not from d20 roll
    name: 'Disease Outbreak',
    icon: '🦠',
    type: 'disease',
    description: 'Sickness spreads through the settlement!'
  };
}
